import { createApi } from '../http/http-common';
import axios, { AxiosResponse } from 'axios';
import {
  ArtworkResultItem,
  CustomerTag,
  PaggedCollection,
  UpdateCustomerArtwork,
} from '@/web-components/logo-library/typings';
import qs from 'qs';
import {
  ApprovalCommand,
  RejectionCommand,
} from '@/libs/proof-approval/typings';
import { Personalization } from '@midocean/webapps-shared-domain';
import {
  ArtworkResult,
  EnrichPositionWithArtworkForm,
  VFileUploaderFile,
} from '@/components/w2pFileUploader';
import {
  authHandler,
  validTokenHandler,
  invalidTokenHandler,
} from './interceptor-handlers/AuthHandler';
import {
  ProofPreview,
  SyncWithSAPCommand,
} from '@/libs/proof-approval/typings/common/Personalization';
import {
  PdfPreviewForm,
  UpdatePersonalizationCommand,
} from '@/web-components/import-wizard/typings/common/createPersonalization';

import {
  CreatePersonalizationForm,
  PersonalizationArtwork,
  Order,
} from '@midocean/webapps-shared-domain';
import { LocaleMessageObject } from 'vue-i18n';
import { NewLogoImprovementRequestForm } from '@/typings/SmallDetails';
import { base64StringToBlob } from 'blob-util';

const customersAPI = createApi({
  BASE_URL: process.env.VUE_APP_CUSTOMERS_BASE_URL,
});
// Setup interceptors for the Customers API
customersAPI.interceptors.request.use(authHandler, function (error) {
  return Promise.reject(error);
});

customersAPI.interceptors.response.use(validTokenHandler, invalidTokenHandler);

// customersAPI.interceptors.response.use(function (response) {
//   // Any status code that lie within the range of 2xx cause this function to trigger
//   return response;
// }, ErrorHandler);

export default {
  //#region Working with CustomerArtworks
  getAll({
    customerId,
    searchBy,
    page = 1,
    pageSize = 8,
  }: {
    customerId: string;
    searchBy: { title?: string; tags?: string[] };
    page: number;
    pageSize: number;
  }): Promise<AxiosResponse<PaggedCollection>> {
    return customersAPI.get(`/customerArtworks`, {
      params: {
        externalCustomerId: customerId,
        tags: searchBy.tags,
        title: searchBy.title,
        page,
        pageSize,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          indices: false,
        });
      },
    });
  },

  getById(id: string): Promise<AxiosResponse<ArtworkResultItem>> {
    return customersAPI.get(`/customerArtworks/${id}`);
  },

  getTranslations(
    languageCode: string
  ): Promise<AxiosResponse<LocaleMessageObject>> {
    return customersAPI.get(`/translationsjson/${languageCode}`);
  },

  update(
    id: string,
    data: UpdateCustomerArtwork
  ): Promise<AxiosResponse<void>> {
    return customersAPI.patch(`customerArtworks/${id}`, data);
  },

  delete(id: string): Promise<AxiosResponse<void>> {
    return customersAPI.delete(`customerArtworks/${id}`);
  },
  //#endregion

  //#region Working with CustomerTags
  getTags(customerId: string): Promise<AxiosResponse<CustomerTag[]>> {
    return customersAPI.get(`/customerTags`, {
      params: {
        customerId,
      },
    });
  },
  //#endregion

  //#region Working with PersonalizationArtworks
  createDraftPersonalizationArtwork(
    customerArtworkId: string
  ): Promise<AxiosResponse<PersonalizationArtwork>> {
    return customersAPI.post(`/personalizationArtwork`, {
      customerArtworkId,
    });
  },
  //#endregion

  //#region Working with Personalizations
  getPersonalization(id: string): Promise<AxiosResponse<Personalization>> {
    return customersAPI.get(`/personalizations/${id}?isEsko=false`);
  },

  submitPersonalization(
    files: File[],
    variantFiles: File[],
    smallDetailsFiles: File[],
    textFiles: File[],
    personalizationForm: CreatePersonalizationForm
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file, file.name);
    }

    for (const file of variantFiles) {
      formData.append('variantFiles', file, file.name);
    }

    if (smallDetailsFiles.length > 0) {
      for (const file of smallDetailsFiles) {
        formData.append('smallDetailsFiles', file, file.name);
      }
    }

    if (textFiles.length > 0) {
      for (const file of textFiles) {
        formData.append('textFiles', file, file.name);
      }
    }

    formData.append('form', JSON.stringify(personalizationForm));

    return customersAPI.post('/personalizations', formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  generateProofPreview(
    files: File[],
    variantFiles: File[],
    smallDetailsFiles: File[],
    textFiles: File[],
    form: PdfPreviewForm
  ): Promise<AxiosResponse<ProofPreview>> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file, file.name);
    }

    for (const file of textFiles) {
      formData.append('textFiles', file, file.name);
    }

    for (const file of variantFiles) {
      formData.append('variantFiles', file, file.name);
    }

    if (smallDetailsFiles.length > 0) {
      for (const file of smallDetailsFiles) {
        formData.append('smallDetailsFiles', file, file.name);
      }
    }

    formData.append('form', JSON.stringify(form));

    return customersAPI.post(
      '/personalizations/generateProofPreview',
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
  },

  completePersonalization(
    personalizationId: string
  ): Promise<AxiosResponse<void>> {
    return customersAPI.post(`/personalizations/${personalizationId}/Complete`);
  },

  updatePersonalization(
    files: File[],
    variantFiles: File[],
    smallDetailsFiles: File[],
    textFiles: File[],
    command: UpdatePersonalizationCommand,
    personalizationId: string,
    proofId: string
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file, file.name);
    }

    for (const file of variantFiles) {
      formData.append('variantFiles', file, file.name);
    }

    for (const file of textFiles) {
      formData.append('textFiles', file, file.name);
    }

    for (const file of smallDetailsFiles) {
      formData.append('smallDetailsFiles', file, file.name);
    }

    formData.append('command', JSON.stringify(command));

    return customersAPI.put(
      `/personalizations/${personalizationId}/proofs/${proofId}`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
  },

  syncWithSAP(
    personalizationId: string,
    command: SyncWithSAPCommand,
    files: VFileUploaderFile[]
  ): Promise<AxiosResponse<void>> {
    const formData = new FormData();

    if (files.length > 0) {
      files.forEach((file, index) => {
        const extension = file.file?.name?.split('.').pop() || 'txt';
        formData.append(
          'files',
          file.file,
          `additional-file-${index + 1}.${extension}`
        );
      });
    }
    formData.append('command', JSON.stringify(command));

    return customersAPI.post(
      `/personalizations/${personalizationId}/SyncWithSAP`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
  },

  toggleCommunication(
    personalizationId: string,
    setCommunicationToCC: boolean
  ): Promise<AxiosResponse<void>> {
    return customersAPI.post(
      `/personalizations/${personalizationId}/CommunicationToCC?setCommunicationToCC=${setCommunicationToCC}`
    );
  },

  createPersonalization(
    artworkResults: ArtworkResult[],
    files: VFileUploaderFile[],
    smallDetailsFiles: VFileUploaderFile[],
    data: CreatePersonalizationForm
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append(
        'files',
        files[i].file,
        `${artworkResults[i].personalizationArtworkId}`
      );

      if (
        artworkResults[i].traditionalTechniquePngFile &&
        artworkResults[i].traditionalTechniquePngFile !== ''
      ) {
        formData.append(
          'files',
          base64StringToBlob(
            artworkResults[i].traditionalTechniquePngFile,
            'image/png'
          ),
          `${artworkResults[i].personalizationArtworkId}-traditional-tech-png`
        );
      }
    }

    for (let i = 0; i < smallDetailsFiles.length; i++) {
      formData.append(
        'smallDetailsFiles',
        smallDetailsFiles[i].file,
        smallDetailsFiles[i].file.name
      );
    }

    formData.append('form', JSON.stringify(data));

    return customersAPI.post('/personalizations', formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  createFullDesignPersonalization(
    file: VFileUploaderFile,
    data: CreatePersonalizationForm
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    formData.append('file', file.file);
    formData.append('json', JSON.stringify(data));

    return customersAPI.post('/personalizations/FullDesign', formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  addArtwork(
    personalizationId: string,
    proofId: string,
    positionCode: string,
    form: EnrichPositionWithArtworkForm
  ): Promise<AxiosResponse<Personalization>> {
    return customersAPI.post(
      `/personalizations/${personalizationId}/proofs/${proofId}/printpositions/${positionCode}/Artworks`,
      form
    );
  },

  approveProof(
    personalizationId: string,
    proofId: string,
    command: ApprovalCommand
  ): Promise<AxiosResponse<Personalization>> {
    return customersAPI.post(
      `personalizations/${personalizationId}/proofs/${proofId}/Approve`,
      command
    );
  },
  rejectProof(
    personalizationId: string,
    proofId: string,
    command: RejectionCommand,
    artworks: VFileUploaderFile[],
    additionalFiles: VFileUploaderFile[]
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    if (artworks.length > 0) {
      artworks.forEach((file, index) => {
        formData.append('files', file?.file, `artwork-${file?.file.name}`);
      });
    }
    if (additionalFiles.length > 0) {
      additionalFiles.forEach((file, index) => {
        const extension = file.file?.name?.split('.').pop() || 'txt';
        formData.append(
          'files',
          file.file,
          `additional-file-${index + 1}.${extension}`
        );
      });
    }
    formData.append('form', JSON.stringify(command));
    return customersAPI.post(
      `personalizations/${personalizationId}/proofs/${proofId}/Reject`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
  },
  enrichProof(
    personalizationId: string,
    proofId: string,
    positionCode: string,
    artwork: unknown
  ): Promise<AxiosResponse<Personalization>> {
    return customersAPI.post(
      `personalizations/${personalizationId}/proofs/${proofId}/printpositions/${positionCode}/Artworks`,
      artwork
    );
  },
  //#endregion

  //#region Working with Orders
  getOrder(orderNumber: string): Promise<AxiosResponse<Order>> {
    return customersAPI.get(`/orders/${orderNumber}`);
  },
  //#endregion

  submitLogoImprovementRequest(
    logos: File[],
    logoImprovementRequestsForm: NewLogoImprovementRequestForm
  ): Promise<AxiosResponse<Personalization>> {
    const formData = new FormData();

    for (const file of logos) {
      formData.append('logos', file, file.name);
    }

    formData.append(
      'newLogoImprovementRequestForm',
      JSON.stringify(logoImprovementRequestsForm)
    );

    return customersAPI.post('/logoImprovementRequests', formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },
};
